import { Form } from "antd"
import type { FormInstance } from "antd"
import React, { type Ref } from "react"

import type { RichTextEditorHandle } from "../../../components/RichTextEditor/RichTextEditor.tsx"
import { focusFirstInvalid } from "../helpers/focus-first-invalid"
import {
  type AutomationFormValues,
  type ScheduleWarning,
  timingPatch,
  touchesCadence
} from "../helpers/form-values"
import { sectionCompletion } from "../helpers/section-completion"
import type { AutomationOptions } from "../types/automation.types"
import { BasicsSection } from "./builder/BasicsSection"
import { BuilderProgress } from "./builder/BuilderProgress"
import { MessageSection } from "./builder/MessageSection"
import { SenderSection } from "./builder/SenderSection"
import { TriggerTimingSection } from "./builder/TriggerTimingSection"

interface AutomationBuilderProps {
  options: AutomationOptions
  form: FormInstance<AutomationFormValues>
  initialValues: AutomationFormValues
  /** The same values the form submits; drives completion and the flow line. */
  values: AutomationFormValues
  onValuesChange: (values: AutomationFormValues) => void
  /** Enter in a single-line field, after the form's rules pass. */
  onSubmit: () => void
  editorRef: Ref<RichTextEditorHandle>
  /** What saving would do to this automation's Nurturing sends. */
  scheduleWarning?: ScheduleWarning | null
}

function AutomationBuilder({
  options,
  form,
  initialValues,
  values,
  onValuesChange,
  onSubmit,
  editorRef,
  scheduleWarning
}: AutomationBuilderProps) {
  // Hidden fields stay in the store, so read all of it, not only what is shown.
  const notifyValues = () => onValuesChange(form.getFieldsValue(true) as AutomationFormValues)
  const completion = sectionCompletion(values, options)

  return (
    <Form<AutomationFormValues>
      form={form}
      layout={"vertical"}
      initialValues={initialValues}
      validateTrigger={["onSubmit", "onChange"]}
      onValuesChange={(changed: Partial<AutomationFormValues>) => {
        const patch = timingPatch(changed)
        if (patch) form.setFieldsValue(patch)
        if (touchesCadence(changed)) form.setFieldsValue({ cadence_exact: null })
        notifyValues()
      }}
      onFinish={() => onSubmit()}
      onFinishFailed={({ errorFields }) =>
        focusFirstInvalid(form, errorFields, () => {
          if (editorRef && typeof editorRef === "object") editorRef.current?.focus()
        })
      }
    >
      <div className={"flex flex-col gap-4"}>
        <BuilderProgress completion={completion} />
        <BasicsSection options={options} complete={completion.basics} />
        <TriggerTimingSection
          options={options}
          values={values}
          complete={completion.trigger}
          scheduleWarning={scheduleWarning}
        />
        <MessageSection
          options={options}
          form={form}
          complete={completion.message}
          editorRef={editorRef}
          onProgrammaticChange={notifyValues}
        />
        <SenderSection options={options} complete={completion.sender} />
      </div>
      {/* The page's actions sit outside the form; this default button is what
          lets Enter in a single-line field submit it. Visually hidden rather
          than display:none, so no engine can skip it as unrendered. */}
      <button type={"submit"} tabIndex={-1} aria-hidden={"true"} className={"sr-only"} />
    </Form>
  )
}

export { type AutomationBuilderProps, AutomationBuilder }
